import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

export type CampaignStatus =
  | "requested"
  | "preparing"
  | "awaiting_approval"
  | "approved"
  | "live"
  | "pause_requested"
  | "paused"
  | "completed"
  | "rejected";
export type ProducerProfile = {
  id: string;
  slug: string;
  name: string;
  photoUrl: string | null;
  profileUrl: string;
  licensedStates: string[];
};
export type Creative = {
  headline: string;
  body: string;
  imageUrl: string;
  landingUrl: string;
};
export type Campaign = {
  id: string;
  userId: string;
  name: string;
  templateId: string;
  product: string;
  states: string[];
  requestedBudgetCents: number;
  dailyLimitCents: number;
  status: CampaignStatus;
  profileSnapshot: ProducerProfile;
  creative: Creative | null;
  creativeVersion: number;
  approvedCreativeVersion: number | null;
  externalAdRefs: { platform: string; campaignId: string; adId?: string }[];
  reporting: {
    impressions?: number;
    clicks?: number;
    reportedAt?: string;
    source?: string;
  } | null;
  fundedCents: number;
  spentCents: number;
  refundedCents: number;
  balanceCents: number;
  leadCount: number;
  pendingRefundCents?: number;
  workflowDraft?: {
    version: number;
    steps: WorkflowStep[];
    testedAt?: string;
    testedVersion?: number;
  } | null;
  workflowPublished?: {
    version: number;
    steps: WorkflowStep[];
    publishedAt: string;
    paused: boolean;
  } | null;
  notes?: string;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};
export type WorkflowStep = { delayMinutes: number; text: string };
export type WorkflowRun = {
  id: string;
  campaignId: string;
  leadId: string;
  workflowVersion: number;
  status: string;
  nextStep: number;
  dueAt: string;
  reason?: string;
  results: {
    step: number;
    status: string;
    messageSid?: string;
    smsHistoryId?: string;
    at: string;
  }[];
  createdAt: string;
  updatedAt: string;
};
export type Workspace = {
  profile: ProducerProfile | null;
  readiness: {
    databaseReady?: boolean;
    /** Licensedproducer account linked (local check — true even if LP is down). */
    accountLinked?: boolean;
    profileLinked: boolean;
    profileAvailable: boolean;
    fundingAvailable: boolean;
    workflowAvailable?: boolean;
    /** A2P brand + campaign approved. Same check the sender enforces. */
    a2pReady?: boolean;
    issues: string[];
  };
  settings: {
    currency: "usd";
    minimumBudgetCents: number;
    minimumDailyLimitCents: number;
    maximumBudgetCents: number;
  };
  templates: {
    id: string;
    name: string;
    product: string;
    description: string;
  }[];
  campaigns: Campaign[];
  isOperator: boolean;
};
export type CampaignDetail = {
  campaign: Campaign;
  events: {
    id: string;
    actorId: string | null;
    type: string;
    createdAt: string;
    data: Record<string, unknown>;
  }[];
  ledger: {
    id: string;
    kind: "funding" | "spend" | "refund";
    amountCents: number;
    reference: string;
    source: string;
    occurredAt: string;
    createdAt: string;
  }[];
  leads: {
    id: string;
    externalId: string;
    leadId: string;
    name?: string;
    status: string;
    createdAt: string;
    attribution: Record<string, string>;
  }[];
  funding: {
    id: string;
    amountCents: number;
    sessionId: string | null;
    paymentIntentId: string | null;
    status: string;
    expiresAt: string;
    createdAt: string;
  }[];
};
export type CampaignRequest = Pick<
  Campaign,
  | "name"
  | "templateId"
  | "product"
  | "states"
  | "requestedBudgetCents"
  | "dailyLimitCents"
> & {
  notes?: string;
  /** Signed authorisation. The server refuses a stale or invented version. */
  acceptance: {
    accepted: true;
    version: string;
    signatureName: string;
    hash?: string;
  };
};
export type OperationsUpdate = {
  status?: CampaignStatus;
  creative?: Creative;
  externalAdRefs?: Campaign["externalAdRefs"];
  reporting?: NonNullable<Campaign["reporting"]>;
  note?: string;
};

// axiosConfig's response interceptor ends with `return response.data`, so every
// call here resolves to the backend's { statusCode, message, data } envelope —
// NOT an AxiosResponse. Axios's types still claim AxiosResponse, so the obvious
// `.data.data` type-checks and then hands back undefined at runtime, which React
// Query rejects outright ("Query data cannot be undefined"). Unwrap once.
const unwrap = <T>(response: unknown): T => (response as { data: T }).data;

async function get<T>(path: string): Promise<T> {
  return unwrap<T>(
    await api.get<{ data: T }>({
      url: getApiUrl(`/managed-marketing${path}`),
    }),
  );
}
async function post<T>(path: string, body: unknown): Promise<T> {
  return unwrap<T>(
    await api.post<{ data: T }>({
      url: getApiUrl(`/managed-marketing${path}`),
      body,
    }),
  );
}
export const managedMarketingApi = {
  saveWorkflow: async (id: string, steps: WorkflowStep[]) =>
    unwrap<Pick<Campaign, "workflowDraft" | "workflowPublished">>(
      await api.put<{
        data: Pick<Campaign, "workflowDraft" | "workflowPublished">;
      }>({
        url: getApiUrl(`/managed-marketing/operations/${id}/workflow`),
        body: { steps },
      }),
    ),
  testWorkflow: (id: string, version: number) =>
    post<{
      version: number;
      valid: boolean;
      previews: WorkflowStep[];
      warnings: string[];
    }>(`/operations/${id}/workflow/test`, { version }),
  publishWorkflow: (id: string, version: number) =>
    post<Pick<Campaign, "workflowDraft" | "workflowPublished">>(
      `/operations/${id}/workflow/publish`,
      { version },
    ),
  pauseWorkflow: (id: string) =>
    post<Pick<Campaign, "workflowDraft" | "workflowPublished">>(
      `/operations/${id}/workflow/pause`,
      {},
    ),
  workflowRuns: (id: string, operator: boolean) =>
    get<{ runs: WorkflowRun[] }>(
      `/${operator ? "operations" : "campaigns"}/${id}/workflow/runs`,
    ),
  cancelWorkflowRun: (id: string, runId: string) =>
    post<WorkflowRun>(`/operations/${id}/workflow/runs/${runId}/cancel`, {
      reason: "Cancelled by campaign operations",
    }),
  workspace: () => get<Workspace>("/workspace"),
  operations: () => get<{ campaigns: Campaign[] }>("/operations"),
  detail: (id: string, operator = false) =>
    get<CampaignDetail>(
      `/${operator ? "operations" : "campaigns"}/${encodeURIComponent(id)}`,
    ),
  create: (body: CampaignRequest) => post<Campaign>("/campaigns", body),
  approve: (id: string, creativeVersion: number) =>
    post<Campaign>(`/campaigns/${id}/approve`, { creativeVersion }),
  pause: (id: string) => post<Campaign>(`/campaigns/${id}/pause`, {}),
  changeRequest: (id: string, message: string) =>
    post<Campaign>(`/campaigns/${id}/change-request`, { message }),
  checkout: (id: string, amountCents: number, requestId: string) =>
    post<{ checkoutUrl: string; sessionId: string }>(
      `/campaigns/${id}/funding-checkout`,
      { amountCents, requestId },
    ),
  update: async (id: string, body: OperationsUpdate) =>
    unwrap<Campaign>(
      await api.patch<{ data: Campaign }>({
        url: getApiUrl(`/managed-marketing/operations/${id}`),
        body,
      }),
    ),
  spend: (
    id: string,
    body: {
      amountCents: number;
      reference: string;
      occurredAt: string;
      source: string;
    },
  ) => post<Campaign>(`/operations/${id}/spend`, body),
  refund: (
    id: string,
    body: { amountCents: number; paymentIntentId: string; requestId: string },
  ) => post<Campaign>(`/operations/${id}/refund`, body),
};
